import {apiRequest, ApiError, type RequestOptions} from "./client";
import {adminAuthHeaders, adminUserFromToken, getAdminToken, isAdminTokenExpired, setAdminToken, shouldRefreshAdminToken} from "./adminAuth";
import {apiPath} from "../config";
import type {
  AdminBrand,
  AdminClientDetail,
  AdminClientGroup,
  AdminClientNotification,
  AdminClientRow,
  AdminClientsResponse,
  AdminCmsAnnouncement,
  AdminCmsCarouselSlide,
  AdminCmsFaqItem,
  AdminCmsHomeBox,
  AdminCmsHubCounts,
  AdminCmsPage,
  AdminDashboardPeriod,
  AdminDashboardStats,
  AdminGoogleAnalytics,
  AdminInfluencerPayoutDetail,
  AdminInfluencerPayoutRow,
  AdminInfluencerRatesResponse,
  AdminIntegrationsHub,
  AdminSymphonyaDashboard,
  AdminSymphonyaConnectionTest,
  AdminListResponse,
  AdminOrderDetail,
  AdminOrderKpis,
  AdminOrderSummary,
  AdminProductDetail,
  AdminProductOffer,
  AdminProductReview,
  AdminProductRow,
  AdminProductShipping,
  AdminProductsResponse,
  AdminReturnDetail,
  AdminReturnSummary,
  AdminSeoEntry,
  AdminAccessRestriction,
  AdminLogRow,
  AdminLogsResponse,
  AdminUserRow,
  AdminUsersResponse,
  AdminUser,
  AdminVoucher,
  AdminVoucherMeta,
  AdminSuggestItem,
  AdminLegacyHub,
  AdminSiteSettings
} from "./admin/types";

function buildQuery(params?: Record<string, string | number | undefined>): string {
  if(!params) return "";
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if(v !== undefined && v !== "") q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

type AdminLoginResponse = AdminUser & {token?: string; expiresIn?: number};

async function adminAuthRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return apiRequest<T>(path, {
    ...options,
    credentials: "include",
    headers: {...adminAuthHeaders(), ...(options.headers || {})}
  });
}

async function adminApiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return apiRequest<T>(path, {
    ...options,
    credentials: "omit",
    headers: {...adminAuthHeaders(), ...(options.headers || {})}
  });
}

async function recoverAdminSession(): Promise<AdminUser | null> {
  try {
    const res = await adminAuthRequest<AdminLoginResponse>("/admin/auth/refresh", {method: "POST"});
    if(res.token) setAdminToken(res.token);
    if(res.authenticated) return res;
    return null;
  } catch {
    try {
      return await adminAuthRequest<AdminUser>("/admin/auth/session");
    } catch {
      return null;
    }
  }
}

export async function adminLogin(mail: string, parola: string): Promise<AdminUser> {
  const res = await adminAuthRequest<AdminLoginResponse>("/admin/auth/login", {method: "POST", body: {mail, parola}});
  if(res.token) setAdminToken(res.token);
  if(res.authenticated) return res;
  const fromToken = adminUserFromToken(res.token || getAdminToken() || "");
  return fromToken ?? {authenticated: false};
}

export async function adminLogout(): Promise<void> {
  try {
    await adminAuthRequest<{ok: boolean}>("/admin/auth/logout", {method: "POST"});
  } finally {
    setAdminToken(null);
  }
}

export async function getAdminSession(): Promise<AdminUser> {
  const token = getAdminToken();
  if(token && !isAdminTokenExpired(token)) {
    const localUser = adminUserFromToken(token);
    try {
      return await adminAuthRequest<AdminUser>("/admin/auth/session");
    } catch(e: unknown) {
      if(e instanceof ApiError && e.status === 401) {
        const recovered = await recoverAdminSession();
        if(recovered) return recovered;
        if(localUser) return localUser;
      } else if(localUser) {
        return localUser;
      }
      throw e;
    }
  }
  const recovered = await recoverAdminSession();
  if(recovered) return recovered;
  setAdminToken(null);
  return {authenticated: false};
}

export async function adminRefreshSession(): Promise<AdminUser> {
  const res = await adminAuthRequest<AdminLoginResponse>("/admin/auth/refresh", {method: "POST"});
  if(res.token) setAdminToken(res.token);
  return res;
}

/** Refresh JWT when close to expiry (sliding 7-day window). */
export async function adminRefreshSessionIfNeeded(): Promise<AdminUser | null> {
  const token = getAdminToken();
  if(!token) {
    const recovered = await recoverAdminSession();
    return recovered;
  }
  if(!shouldRefreshAdminToken(token) && !isAdminTokenExpired(token)) {
    return adminUserFromToken(token);
  }
  try {
    return await adminRefreshSession();
  } catch {
    if(isAdminTokenExpired(token)) {
      const recovered = await recoverAdminSession();
      return recovered;
    }
    return adminUserFromToken(token);
  }
}

export async function adminRecoverPassword(mail: string): Promise<{ok: boolean}> {
  return apiRequest<{ok: boolean}>("/admin/auth/recover", {method: "POST", body: {mail}, credentials: "omit"});
}

export async function adminChangePassword(parola: string): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>("/admin/auth/password", {method: "POST", body: {parola}});
}

export async function adminHeartbeat(url: string): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>("/admin/auth/heartbeat", {method: "POST", body: {url}});
}

export async function getAdminDashboard(params?: {period?: AdminDashboardPeriod}): Promise<AdminDashboardStats> {
  return adminApiRequest<AdminDashboardStats>(`/admin/dashboard/stats${buildQuery({period: params?.period})}`);
}

export async function getAdminOrderKpis(): Promise<AdminOrderKpis> {
  return adminApiRequest<AdminOrderKpis>("/admin/orders/stats");
}

export async function listAdminOrders(params?: {
  page?: number;
  pageSize?: number;
  status?: number;
  clientId?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}): Promise<AdminListResponse<AdminOrderSummary>> {
  return adminApiRequest<AdminListResponse<AdminOrderSummary>>(`/admin/orders${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function getAdminOrder(id: number): Promise<AdminOrderDetail> {
  return adminApiRequest<AdminOrderDetail>(`/admin/orders/${id}`);
}

export async function updateAdminOrderStatus(id: number, operation: string, extra?: {respingere?: string}): Promise<AdminOrderDetail> {
  return adminApiRequest<AdminOrderDetail>(`/admin/orders/${id}/status`, {method: "POST", body: {operation, ...extra}});
}

export async function updateAdminOrderDelivery(id: number, data: {
  curier?: number;
  awb?: string;
  cost?: number;
  adresa?: string;
  adresa_judet?: string;
  adresa_localitate?: string;
  adresa_codpostal?: string;
}): Promise<AdminOrderDetail> {
  return adminApiRequest<AdminOrderDetail>(`/admin/orders/${id}/delivery`, {method: "PATCH", body: data});
}

export async function updateAdminOrderLines(
  id: number,
  lines: {productId: number; quantity: number}[]
): Promise<AdminOrderDetail> {
  return adminApiRequest<AdminOrderDetail>(`/admin/orders/${id}/lines`, {method: "PUT", body: {lines}});
}

export async function listAdminReturns(params?: {
  page?: number;
  pageSize?: number;
  status?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}): Promise<AdminListResponse<AdminReturnSummary>> {
  return adminApiRequest<AdminListResponse<AdminReturnSummary>>(`/admin/returns${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function getAdminReturn(id: number): Promise<AdminReturnDetail> {
  return adminApiRequest<AdminReturnDetail>(`/admin/returns/${id}`);
}

export async function updateAdminReturnStatus(id: number, operation: string, extra?: {respingere?: string}): Promise<AdminReturnDetail> {
  return adminApiRequest<AdminReturnDetail>(`/admin/returns/${id}/status`, {method: "POST", body: {operation, ...extra}});
}

export async function deleteAdminReturn(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/returns/${id}`, {method: "DELETE"});
}

export async function listAdminVouchers(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminVoucher>> {
  return adminApiRequest<AdminListResponse<AdminVoucher>>(`/admin/vouchers${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function getAdminVoucherMeta(): Promise<AdminVoucherMeta> {
  return adminApiRequest<AdminVoucherMeta>("/admin/vouchers/meta");
}

export async function getAdminVoucher(id: number): Promise<AdminVoucher> {
  return adminApiRequest<AdminVoucher>(`/admin/vouchers/${id}`);
}

export async function createAdminVoucher(data: Partial<AdminVoucher> & {categorie?: number[]; subcategorie?: number[]; brand?: number[]}): Promise<AdminVoucher> {
  return adminApiRequest<AdminVoucher>("/admin/vouchers", {method: "POST", body: data});
}

export async function updateAdminVoucher(id: number, data: Partial<AdminVoucher> & {categorie?: number[]; subcategorie?: number[]; brand?: number[]}): Promise<AdminVoucher> {
  return adminApiRequest<AdminVoucher>(`/admin/vouchers/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminVoucher(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/vouchers/${id}`, {method: "DELETE"});
}

export function adminLegacyIframeUrl(base: string, page: string, params: Record<string, string | number> = {}): string {
  const q = new URLSearchParams({page});
  Object.entries(params).forEach(([k, v]) => q.set(k, String(v)));
  return `${base.replace(/\/$/, "")}/header?${q.toString()}`;
}

export async function getAdminLegacyHub(): Promise<AdminLegacyHub> {
  return adminApiRequest<AdminLegacyHub>("/admin/tools/legacy-hub");
}

export async function setAdminPageSize(pageSize: number): Promise<{pageSize: number}> {
  return adminApiRequest<{pageSize: number}>("/admin/settings/page-size", {method: "PATCH", body: {pageSize}});
}

export async function adminSuggest(phrase: string): Promise<{items: AdminSuggestItem[]}> {
  return adminApiRequest<{items: AdminSuggestItem[]}>(`/admin/suggest${buildQuery({q: phrase})}`);
}

export async function lookupAdminProductByCode(cod: string): Promise<AdminSuggestItem> {
  return adminApiRequest<AdminSuggestItem>(`/admin/products/lookup${buildQuery({cod})}`);
}

export async function removeAdminOrderDiscount(id: number): Promise<{ok: boolean; orderId: number}> {
  return adminApiRequest<{ok: boolean; orderId: number}>(`/admin/order-discounts/${id}`, {method: "DELETE"});
}

export async function uploadAdminMedia(params: {
  folder: string;
  table: string;
  column: string;
  id: number;
  file: File;
  insert?: boolean;
}): Promise<{filename: string; url: string}> {
  const q = new URLSearchParams({
    folder: params.folder,
    table: params.table,
    column: params.column,
    id: String(params.id)
  });
  if(params.insert) q.set("insert", "1");
  const fd = new FormData();
  fd.append("file", params.file);
  const res = await fetch(apiPath(`/admin/media/upload?${q.toString()}`), {method: "POST", body: fd, credentials: "omit", headers: adminAuthHeaders()});
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if(!res.ok) throw new Error((data as {error?: string})?.error || "Upload failed");
  return data as {filename: string; url: string};
}

export async function deleteAdminMedia(params: {
  folder: string;
  file: string;
  table: string;
  column: string;
  id: number;
}): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>("/admin/media/delete", {method: "POST", body: params});
}

export async function listAdminProducts(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<AdminProductsResponse> {
  return adminApiRequest<AdminProductsResponse>(`/admin/products${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function getAdminProduct(id: number): Promise<AdminProductDetail> {
  return adminApiRequest<AdminProductDetail>(`/admin/products/${id}`);
}

export async function createAdminProduct(data: Partial<AdminProductDetail>): Promise<AdminProductDetail> {
  return adminApiRequest<AdminProductDetail>("/admin/products", {method: "POST", body: data});
}

export async function updateAdminProduct(id: number, data: Partial<AdminProductDetail>): Promise<AdminProductDetail> {
  return adminApiRequest<AdminProductDetail>(`/admin/products/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminProduct(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/products/${id}`, {method: "DELETE"});
}

export async function listAdminClients(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  firma?: string;
  comenzi?: string | number;
}): Promise<AdminClientsResponse> {
  return adminApiRequest<AdminClientsResponse>(`/admin/clients${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function listAdminClientFirme(): Promise<{items: string[]}> {
  return adminApiRequest<{items: string[]}>("/admin/clients/firme");
}

export async function listAdminBrands(): Promise<{items: AdminBrand[]}> {
  return adminApiRequest<{items: AdminBrand[]}>("/admin/brands");
}

export function adminClientsExportUrl(): string {
  return apiPath("/admin/clients/export");
}

export async function getAdminClient(id: number): Promise<AdminClientDetail> {
  return adminApiRequest<AdminClientDetail>(`/admin/clients/${id}`);
}

export async function createAdminClient(data: Partial<AdminClientDetail>): Promise<AdminClientDetail> {
  return adminApiRequest<AdminClientDetail>("/admin/clients", {method: "POST", body: data});
}

export async function updateAdminClient(id: number, data: Partial<AdminClientDetail>): Promise<AdminClientDetail> {
  return adminApiRequest<AdminClientDetail>(`/admin/clients/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminClient(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/clients/${id}`, {method: "DELETE"});
}

export async function listAdminProductOffers(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminProductOffer>> {
  return adminApiRequest<AdminListResponse<AdminProductOffer>>(`/admin/product-offers${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminProductOffer(data: Partial<AdminProductOffer>): Promise<AdminProductOffer> {
  return adminApiRequest<AdminProductOffer>("/admin/product-offers", {method: "POST", body: data});
}

export async function updateAdminProductOffer(id: number, data: Partial<AdminProductOffer>): Promise<AdminProductOffer> {
  return adminApiRequest<AdminProductOffer>(`/admin/product-offers/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminProductOffer(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/product-offers/${id}`, {method: "DELETE"});
}

export async function listAdminProductReviews(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminProductReview>> {
  return adminApiRequest<AdminListResponse<AdminProductReview>>(`/admin/product-reviews${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminProductReview(data: Partial<AdminProductReview>): Promise<AdminProductReview> {
  return adminApiRequest<AdminProductReview>("/admin/product-reviews", {method: "POST", body: data});
}

export async function updateAdminProductReview(id: number, data: Partial<AdminProductReview>): Promise<AdminProductReview> {
  return adminApiRequest<AdminProductReview>(`/admin/product-reviews/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminProductReview(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/product-reviews/${id}`, {method: "DELETE"});
}

export async function listAdminProductShipping(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminProductShipping>> {
  return adminApiRequest<AdminListResponse<AdminProductShipping>>(`/admin/product-shipping${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminProductShipping(data: {productId: number}): Promise<AdminProductShipping> {
  return adminApiRequest<AdminProductShipping>("/admin/product-shipping", {method: "POST", body: data});
}

export async function updateAdminProductShipping(id: number, data: {productId: number}): Promise<AdminProductShipping> {
  return adminApiRequest<AdminProductShipping>(`/admin/product-shipping/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminProductShipping(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/product-shipping/${id}`, {method: "DELETE"});
}

export async function listAdminClientGroups(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminClientGroup>> {
  return adminApiRequest<AdminListResponse<AdminClientGroup>>(`/admin/client-groups${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminClientGroup(data: Partial<AdminClientGroup>): Promise<AdminClientGroup> {
  return adminApiRequest<AdminClientGroup>("/admin/client-groups", {method: "POST", body: data});
}

export async function updateAdminClientGroup(id: number, data: Partial<AdminClientGroup>): Promise<AdminClientGroup> {
  return adminApiRequest<AdminClientGroup>(`/admin/client-groups/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminClientGroup(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/client-groups/${id}`, {method: "DELETE"});
}

export async function listAdminClientNotifications(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminClientNotification>> {
  return adminApiRequest<AdminListResponse<AdminClientNotification>>(`/admin/client-notifications${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function updateAdminClientNotification(id: number, data: Partial<AdminClientNotification>): Promise<AdminClientNotification> {
  return adminApiRequest<AdminClientNotification>(`/admin/client-notifications/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminClientNotification(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/client-notifications/${id}`, {method: "DELETE"});
}

export async function getAdminCmsHubCounts(): Promise<AdminCmsHubCounts> {
  return adminApiRequest<AdminCmsHubCounts>("/admin/cms/hub-counts");
}

export async function listAdminCmsPages(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminCmsPage>> {
  return adminApiRequest<AdminListResponse<AdminCmsPage>>(`/admin/cms/pages${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminCmsPage(data: Partial<AdminCmsPage>): Promise<AdminCmsPage> {
  return adminApiRequest<AdminCmsPage>("/admin/cms/pages", {method: "POST", body: data});
}

export async function updateAdminCmsPage(id: number, data: Partial<AdminCmsPage>): Promise<AdminCmsPage> {
  return adminApiRequest<AdminCmsPage>(`/admin/cms/pages/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminCmsPage(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/cms/pages/${id}`, {method: "DELETE"});
}

export async function listAdminCmsHomeBoxes(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminCmsHomeBox>> {
  return adminApiRequest<AdminListResponse<AdminCmsHomeBox>>(`/admin/cms/home-boxes${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminCmsHomeBox(data: Partial<AdminCmsHomeBox>): Promise<AdminCmsHomeBox> {
  return adminApiRequest<AdminCmsHomeBox>("/admin/cms/home-boxes", {method: "POST", body: data});
}

export async function updateAdminCmsHomeBox(id: number, data: Partial<AdminCmsHomeBox>): Promise<AdminCmsHomeBox> {
  return adminApiRequest<AdminCmsHomeBox>(`/admin/cms/home-boxes/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminCmsHomeBox(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/cms/home-boxes/${id}`, {method: "DELETE"});
}

export async function listAdminCmsCarousel(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminCmsCarouselSlide>> {
  return adminApiRequest<AdminListResponse<AdminCmsCarouselSlide>>(`/admin/cms/carousel${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminCmsCarouselSlide(data: Partial<AdminCmsCarouselSlide>): Promise<AdminCmsCarouselSlide> {
  return adminApiRequest<AdminCmsCarouselSlide>("/admin/cms/carousel", {method: "POST", body: data});
}

export async function updateAdminCmsCarouselSlide(id: number, data: Partial<AdminCmsCarouselSlide>): Promise<AdminCmsCarouselSlide> {
  return adminApiRequest<AdminCmsCarouselSlide>(`/admin/cms/carousel/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminCmsCarouselSlide(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/cms/carousel/${id}`, {method: "DELETE"});
}

export async function listAdminCmsAnnouncements(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminCmsAnnouncement>> {
  return adminApiRequest<AdminListResponse<AdminCmsAnnouncement>>(`/admin/cms/announcements${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminCmsAnnouncement(data: Partial<AdminCmsAnnouncement>): Promise<AdminCmsAnnouncement> {
  return adminApiRequest<AdminCmsAnnouncement>("/admin/cms/announcements", {method: "POST", body: data});
}

export async function updateAdminCmsAnnouncement(id: number, data: Partial<AdminCmsAnnouncement>): Promise<AdminCmsAnnouncement> {
  return adminApiRequest<AdminCmsAnnouncement>(`/admin/cms/announcements/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminCmsAnnouncement(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/cms/announcements/${id}`, {method: "DELETE"});
}

export async function listAdminCmsFaq(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminCmsFaqItem>> {
  return adminApiRequest<AdminListResponse<AdminCmsFaqItem>>(`/admin/cms/faq${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminCmsFaqItem(data: Partial<AdminCmsFaqItem>): Promise<AdminCmsFaqItem> {
  return adminApiRequest<AdminCmsFaqItem>("/admin/cms/faq", {method: "POST", body: data});
}

export async function updateAdminCmsFaqItem(id: number, data: Partial<AdminCmsFaqItem>): Promise<AdminCmsFaqItem> {
  return adminApiRequest<AdminCmsFaqItem>(`/admin/cms/faq/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminCmsFaqItem(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/cms/faq/${id}`, {method: "DELETE"});
}

export async function listAdminSeoEntries(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminListResponse<AdminSeoEntry>> {
  return adminApiRequest<AdminListResponse<AdminSeoEntry>>(`/admin/seo/entries${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function createAdminSeoEntry(data: Partial<AdminSeoEntry>): Promise<AdminSeoEntry> {
  return adminApiRequest<AdminSeoEntry>("/admin/seo/entries", {method: "POST", body: data});
}

export async function updateAdminSeoEntry(id: number, data: Partial<AdminSeoEntry>): Promise<AdminSeoEntry> {
  return adminApiRequest<AdminSeoEntry>(`/admin/seo/entries/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminSeoEntry(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/seo/entries/${id}`, {method: "DELETE"});
}

export async function listAdminSeoPageFiles(): Promise<{items: string[]}> {
  return adminApiRequest<{items: string[]}>("/admin/seo/page-files");
}

export function adminSeoExportUrl(): string {
  return apiPath("/admin/seo/export");
}

export async function getAdminGoogleAnalytics(): Promise<AdminGoogleAnalytics> {
  return adminApiRequest<AdminGoogleAnalytics>("/admin/seo/google-analytics");
}

export async function updateAdminGoogleAnalytics(code: string): Promise<AdminGoogleAnalytics> {
  return adminApiRequest<AdminGoogleAnalytics>("/admin/seo/google-analytics", {method: "PATCH", body: {code}});
}

export async function regenerateAdminProductSearch(): Promise<{ok: boolean; updated: number; total: number}> {
  return adminApiRequest<{ok: boolean; updated: number; total: number}>("/admin/maintenance/regenerate-product-search", {method: "POST"});
}

export async function getAdminIntegrationsHub(): Promise<AdminIntegrationsHub> {
  return adminApiRequest<AdminIntegrationsHub>("/admin/integrations");
}

export async function getAdminSymphonyaDashboard(): Promise<AdminSymphonyaDashboard> {
  return adminApiRequest<AdminSymphonyaDashboard>("/admin/integrations/symphonya/dashboard");
}

export async function testAdminSymphonyaConnection(): Promise<AdminSymphonyaConnectionTest> {
  return adminApiRequest<AdminSymphonyaConnectionTest>("/admin/integrations/symphonya/test-connection");
}

export async function syncAdminSymphonyaProducts(): Promise<{ok: boolean; job: string; durationMs?: number}> {
  return adminApiRequest<{ok: boolean; job: string; durationMs?: number}>("/admin/integrations/symphonya/sync-products", {method: "POST"});
}

export async function sendAdminSymphonyaOrders(orderId = 0): Promise<{
  ok: boolean;
  job: string;
  orderId?: number;
  symphonya?: number;
  symphonyaError?: string;
  error?: string;
  durationMs?: number;
  pendingLeft?: number;
}> {
  return adminApiRequest("/admin/integrations/symphonya/send-orders", {method: "POST", body: orderId > 0 ? {orderId} : {}});
}

export async function generateAdminAwbSameday(orderId: number): Promise<{ok: boolean; awb: string; message: string}> {
  return adminApiRequest("/admin/integrations/orders/"+orderId+"/awb/sameday", {method: "POST"});
}

export async function listAdminUsers(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminUsersResponse> {
  return adminApiRequest<AdminUsersResponse>(`/admin/users${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function getAdminUser(id: number): Promise<AdminUserRow> {
  return adminApiRequest<AdminUserRow>(`/admin/users/${id}`);
}

export async function createAdminUser(data: Partial<AdminUserRow> & {parola: string}): Promise<AdminUserRow> {
  return adminApiRequest<AdminUserRow>("/admin/users", {method: "POST", body: data});
}

export async function updateAdminUser(id: number, data: Partial<AdminUserRow> & {parola?: string}): Promise<AdminUserRow> {
  return adminApiRequest<AdminUserRow>(`/admin/users/${id}`, {method: "PATCH", body: data});
}

export async function deleteAdminUser(id: number): Promise<{ok: boolean}> {
  return adminApiRequest<{ok: boolean}>(`/admin/users/${id}`, {method: "DELETE"});
}

export async function listAdminLogs(params?: {page?: number; pageSize?: number; search?: string}): Promise<AdminLogsResponse> {
  return adminApiRequest<AdminLogsResponse>(`/admin/log${buildQuery(params as Record<string, string | number | undefined>)}`);
}

export async function getAdminLog(id: number): Promise<AdminLogRow> {
  return adminApiRequest<AdminLogRow>(`/admin/log/${id}`);
}

export function adminLogExportUrl(): string {
  return apiPath("/admin/log/export");
}

export async function getAdminAccessRestriction(): Promise<AdminAccessRestriction> {
  return adminApiRequest<AdminAccessRestriction>("/admin/log/access-restriction");
}

export async function updateAdminAccessRestriction(ips: string): Promise<AdminAccessRestriction> {
  return adminApiRequest<AdminAccessRestriction>("/admin/log/access-restriction", {method: "PATCH", body: {ips}});
}

export async function banAdminIp(ip: string): Promise<{ok: boolean; blockedIps: string[]}> {
  return adminApiRequest("/admin/log/ban-ip", {method: "POST", body: {ip}});
}

export async function unbanAdminIp(ip: string): Promise<{ok: boolean; blockedIps: string[]}> {
  return adminApiRequest("/admin/log/unban-ip", {method: "POST", body: {ip}});
}

export async function getAdminSiteSettings(): Promise<AdminSiteSettings> {
  return adminApiRequest<AdminSiteSettings>("/admin/settings/site");
}

export async function updateAdminSiteSettings(data: Partial<AdminSiteSettings>): Promise<AdminSiteSettings> {
  return adminApiRequest<AdminSiteSettings>("/admin/settings/site", {method: "PATCH", body: data});
}

export async function getAdminInfluencerRates(): Promise<AdminInfluencerRatesResponse> {
  return adminApiRequest<AdminInfluencerRatesResponse>("/admin/influencer/rates");
}

export async function upsertAdminInfluencerRate(data: {
  scopeType: string;
  scopeId: number;
  percent: number;
  activ?: boolean | number;
}): Promise<AdminInfluencerRatesResponse> {
  return adminApiRequest<AdminInfluencerRatesResponse>("/admin/influencer/rates", {method: "POST", body: data});
}

export async function deleteAdminInfluencerRate(id: number): Promise<AdminInfluencerRatesResponse> {
  return adminApiRequest<AdminInfluencerRatesResponse>(`/admin/influencer/rates/${id}`, {method: "DELETE"});
}

export async function listAdminInfluencerPayouts(params?: {
  page?: number;
  pageSize?: number;
  status?: string;
}): Promise<AdminListResponse<AdminInfluencerPayoutRow>> {
  return adminApiRequest<AdminListResponse<AdminInfluencerPayoutRow>>(
    `/admin/influencer/payouts${buildQuery(params as Record<string, string | number | undefined>)}`
  );
}

export async function getAdminInfluencerPayout(id: number): Promise<AdminInfluencerPayoutDetail> {
  return adminApiRequest<AdminInfluencerPayoutDetail>(`/admin/influencer/payouts/${id}`);
}

export async function updateAdminInfluencerPayout(
  id: number,
  data: {status: string; adminNote?: string}
): Promise<AdminInfluencerPayoutDetail> {
  return adminApiRequest<AdminInfluencerPayoutDetail>(`/admin/influencer/payouts/${id}`, {method: "PATCH", body: data});
}

export * from "./admin/types";
export * from "./admin/symphonya";
